// Deterministic replays of a stored conversation for Lesson 4.8 ("A Conversation Is State the
// Server Must Keep"). Every answer, row count and token count below was recorded from real runs
// on 8 October 2026 with zai.glm-5 over the read tools of Unit 2. The graphics replay those
// recordings and never call a model. The graphic ids keep the `state-` prefix; the file is named
// after the conversation because `state-labs.tsx` already holds the Module 1 graphics.

import { abridge, formatTokens } from './know-models';

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const TURN_1_QUESTION = 'What is open in Apollo?';
export const TURN_2_QUESTION = 'Who owns the first one?';
export const TURN_1_ANSWER =
  'Apollo has 6 open tasks: "Write the release note" (assigned to Ravi), "Check the dates", "Order ten more name badges", "Check the projector in the small room", "Send the final headcount to the venue", and "Write the release note for Apollo" (all unassigned).';
export const TURN_2_ANSWER = 'The first task "Write the release note" is owned by Ravi.';
export const FRESH_ANSWER =
  'The Apollo project has three members: Maya (manager), Ravi (member), and Jo (member). Could you clarify which "first one" you\'re referring to?';

// --- state-rows: what one turn adds to the table ------------------------------------------

export const TURNS = ['1', '2'] as const;
export type Turn = (typeof TURNS)[number];
export const turnLabels: Record<Turn, string> = {
  '1': `1 ${TURN_1_QUESTION}`,
  '2': `2 ${TURN_2_QUESTION}`,
};
export const toTurn = (value: string): Turn => TURNS.find((turn) => turn === value) ?? '1';
export type Role = 'user' | 'assistant' | 'tool';
export interface Row {
  position: number;
  role: Role;
  /** The content, or the tool request an assistant row carries. */
  content: string;
}
const row = (position: number, role: Role, content: string): Row => ({ position, role, content });
/** The rows each turn appended, one per message, in order. */
export const turnRows: Record<Turn, readonly Row[]> = {
  '1': [
    row(1, 'user', TURN_1_QUESTION),
    row(2, 'assistant', 'requests list_projects()'),
    row(3, 'tool', 'Apollo (1), Support (2)'),
    row(4, 'assistant', 'requests list_tasks(project_id 1)'),
    row(5, 'tool', '6 open tasks'),
    row(6, 'assistant', TURN_1_ANSWER),
  ],
  '2': [row(7, 'user', TURN_2_QUESTION), row(8, 'assistant', TURN_2_ANSWER)],
};
export const rowsAdded = (turn: Turn) => turnRows[turn].length;
export const modelCalls = (turn: Turn) =>
  turnRows[turn].filter((entry) => entry.role === 'assistant').length;
export const toolRows = (turn: Turn) =>
  turnRows[turn].filter((entry) => entry.role === 'tool').length;
export interface RowsState {
  turn: Turn;
  stored: boolean;
}
export const initialRows = (): RowsState => ({ turn: '1', stored: false });
export type RowsAction = { type: 'turn'; value: Turn } | { type: 'store' } | { type: 'reset' };
export function rowsTransition(state: RowsState, action: RowsAction): RowsState {
  switch (action.type) {
    case 'reset':
      return initialRows();
    case 'turn':
      return { turn: action.value, stored: false };
    case 'store':
      return { ...state, stored: true };
  }
}
export const ROWS_START_MESSAGE = 'Choose a turn, then press Store the turn.';
export const rowsMessages: Record<Turn, string> = {
  '1': 'Six rows, one per message, and both tool results are among them.',
  '2': 'Two rows, and the answer came from the stored rows of turn 1.',
};
export const ROWS_COUNTEREXAMPLE =
  'Without the tool rows a replay asks the model to answer about results it never saw.';
export const ROWS_REFINEMENT = 'Refinement: store every message, tool results included.';
export function rowsSummary(state: RowsState): Summary {
  if (!state.stored) return { tone: 'neutral', message: ROWS_START_MESSAGE };
  return { tone: 'success', message: rowsMessages[state.turn] };
}

// --- state-resume: does the assistant remember after a reload? -----------------------------

export const HOMES = ['page', 'rows'] as const;
export type Home = (typeof HOMES)[number];
export const homeLabels: Record<Home, string> = {
  page: 'in the page',
  rows: 'in rows on the server',
};
export const toHome = (value: string): Home => HOMES.find((home) => home === value) ?? 'page';
export const SYSTEM_LINE = 'system: the assistant rule and the tools';
export const messageLine = (entry: Row) => `${entry.role}: ${abridge(entry.content)}`;
/** The messages the next call carried after the reload. */
export const sentMessages = (home: Home): string[] =>
  home === 'page'
    ? [SYSTEM_LINE, `user: ${TURN_2_QUESTION}`]
    : [SYSTEM_LINE, ...turnRows['1'].map(messageLine), `user: ${TURN_2_QUESTION}`];
export const sentText = (home: Home) => sentMessages(home).join('\n');
export interface ResumeRun {
  replayed: number;
  lookups: number;
  answered: 'no' | 'yes';
  answer: string;
  tone: Tone;
}
export const resumeRuns: Record<Home, ResumeRun> = {
  page: { replayed: 0, lookups: 2, answered: 'no', answer: FRESH_ANSWER, tone: 'warning' },
  rows: { replayed: 6, lookups: 0, answered: 'yes', answer: TURN_2_ANSWER, tone: 'success' },
};
export interface ResumeState {
  home: Home;
  reloaded: boolean;
}
export const initialResume = (): ResumeState => ({ home: 'page', reloaded: false });
export type ResumeAction = { type: 'home'; value: Home } | { type: 'reload' } | { type: 'reset' };
export function resumeTransition(state: ResumeState, action: ResumeAction): ResumeState {
  switch (action.type) {
    case 'reset':
      return initialResume();
    case 'home':
      return { home: action.value, reloaded: false };
    case 'reload':
      return { ...state, reloaded: true };
  }
}
export const RESUME_START_MESSAGE =
  'Choose where the conversation lives, then press Reload, then ask who owns the first one.';
export const RESUME_PAGE_MESSAGE =
  'The page held the only copy, so the model saw a fresh question and asked back.';
export const RESUME_ROWS_MESSAGE =
  'The six rows came back from the server, and the model answered in one step.';
export const RESUME_REFINEMENT = 'Refinement: replay from rows.';
export function resumeSummary(state: ResumeState): Summary {
  if (!state.reloaded) return { tone: 'neutral', message: RESUME_START_MESSAGE };
  return state.home === 'page'
    ? { tone: 'warning', message: RESUME_PAGE_MESSAGE }
    : { tone: 'success', message: RESUME_ROWS_MESSAGE };
}

// --- state-compact: what a summary saves and loses -----------------------------------------

export const TURN_COUNTS = ['2', '6'] as const;
export type TurnCount = (typeof TURN_COUNTS)[number];
export const toTurnCount = (value: string): TurnCount =>
  TURN_COUNTS.find((count) => count === value) ?? '2';
/** Prompt tokens of the first call of that turn, recorded. */
export const promptTokens: Record<TurnCount, number> = { '2': 773, '6': 1030 };
/** Stored rows replayed before that turn's question. */
export const replayedRows: Record<TurnCount, number> = { '2': 6, '6': 18 };
/** Fewer than this many messages are never folded. */
export const FOLD_AT = 8;
/** Messages kept verbatim after the summary row. */
export const KEPT_VERBATIM = 8;
export const SUMMARY_TOKENS = 90;
export const COMPACT_TOKENS = 600;
export const RECORDED_SUMMARY =
  'The user asked about open tasks and membership in two projects: Apollo and Support. For Apollo, there are 6 open tasks including "Write the release note" (task 9, owned by Ravi), and the project has 3 members (Maya as manager, Ravi and Jo as members). For Support, there is 1 open task ("Answer the refund tickets" assigned to Jo), and the project has 2 members (Maya as manager and Jo as member). The user also confirmed that the headcount task in Apollo is not done.';
export const DROPPED_TITLES = [
  'Check the dates',
  'Order ten more name badges',
  'Check the projector in the small room',
  'Send the final headcount to the venue',
  'Write the release note for Apollo',
] as const;
export const DROPPED_TEXT = `Dropped: ${DROPPED_TITLES.map((title) => `"${title}"`).join(', ')}.`;
export interface CompactState {
  turns: TurnCount;
  summarize: boolean;
}
export const initialCompact = (): CompactState => ({ turns: '2', summarize: false });
export type CompactAction =
  { type: 'turns'; value: TurnCount } | { type: 'toggle-summary' } | { type: 'reset' };
export function compactTransition(state: CompactState, action: CompactAction): CompactState {
  switch (action.type) {
    case 'reset':
      return initialCompact();
    case 'turns':
      return { ...state, turns: action.value };
    case 'toggle-summary':
      return { ...state, summarize: !state.summarize };
  }
}
export const folded = (state: CompactState) =>
  state.summarize && replayedRows[state.turns] >= FOLD_AT;
export const compactTokens = (state: CompactState) =>
  folded(state) ? `about ${COMPACT_TOKENS}` : formatTokens(promptTokens[state.turns]);
export const compactReplayed = (state: CompactState) =>
  folded(state) ? KEPT_VERBATIM + 1 : replayedRows[state.turns];
export const compactSummaryFact = (state: CompactState) => {
  if (folded(state)) return `four sentences, about ${SUMMARY_TOKENS} tokens`;
  return state.summarize ? 'not needed yet' : 'none';
};
export const NO_SUMMARY = '(no summary row)';
export const compactSummaryText = (state: CompactState) =>
  folded(state) ? RECORDED_SUMMARY : NO_SUMMARY;
export const COMPACT_TWO_MESSAGE =
  'Six messages replayed cost 773 prompt tokens, and nothing is folded yet.';
export const COMPACT_TWO_SUMMARY_MESSAGE =
  'Fewer than eight messages are never folded, so the prompt stays at 773 tokens.';
export const COMPACT_SIX_MESSAGE =
  'Eighteen messages replayed cost 1,030 prompt tokens, and every turn adds more.';
export const COMPACT_SIX_SUMMARY_MESSAGE =
  'A summary row and the last eight messages cost about 600 tokens.';
export const COMPACT_COUNTEREXAMPLE =
  'The summary lost the badges task title, which the next question needs.';
export const COMPACT_REFINEMENT =
  'Refinement: keep the last turns verbatim, summarize the rest, let the tools recover details.';
export function compactSummary(state: CompactState): Summary {
  if (state.turns === '2')
    return {
      tone: 'neutral',
      message: state.summarize ? COMPACT_TWO_SUMMARY_MESSAGE : COMPACT_TWO_MESSAGE,
    };
  return folded(state)
    ? { tone: 'success', message: COMPACT_SIX_SUMMARY_MESSAGE }
    : { tone: 'warning', message: COMPACT_SIX_MESSAGE };
}

// --- state-idempotent: the same turn sent twice --------------------------------------------

export const SENDS = ['once', 'twice', 'retry'] as const;
export type Send = (typeof SENDS)[number];
export const sendLabels: Record<Send, string> = {
  once: 'one send',
  twice: 'the button is pressed twice',
  retry: 'a retry after a timeout',
};
export const toSend = (value: string): Send => SENDS.find((send) => send === value) ?? 'once';
export const ROWS_PER_RUN = 6;
export const KEY = 'turn-7f3a';
export const NO_KEY = 'none';
export interface Request {
  request: string;
  key: string;
  server: string;
}
export const RUNS = `runs, writes ${ROWS_PER_RUN} rows, returns the answer`;
export const RUNS_AGAIN = `runs again, writes ${ROWS_PER_RUN} more rows, returns the answer again`;
export const TIMES_OUT = 'runs, the page times out before the answer';
export const STORED = 'finds the key, returns the stored answer';
export const STILL_RUNNING = 'finds the key still running, says still working, ask again';
/** The requests the server saw and what it did with each. */
export function requests(send: Send, keyed: boolean): Request[] {
  const key = keyed ? KEY : NO_KEY;
  const first = { request: '1 POST turn', key, server: send === 'retry' ? TIMES_OUT : RUNS };
  if (send === 'once') return [first];
  const second = { request: '2 POST turn, the same', key, server: RUNS_AGAIN };
  if (keyed) second.server = send === 'twice' ? STORED : STILL_RUNNING;
  return [first, second];
}
export const modelRuns = (send: Send, keyed: boolean) => (send === 'once' || keyed ? 1 : 2);
export const rowsWritten = (send: Send, keyed: boolean) => modelRuns(send, keyed) * ROWS_PER_RUN;
export function answerReturned(send: Send, keyed: boolean) {
  if (send === 'once') return 'the answer';
  if (!keyed) return 'the answer twice';
  return send === 'twice' ? 'the stored answer' : 'still working, ask again';
}
export interface IdempotentState {
  send: Send;
  keyed: boolean;
  sent: boolean;
}
export const initialIdempotent = (): IdempotentState => ({
  send: 'once',
  keyed: false,
  sent: false,
});
export type IdempotentAction =
  { type: 'send'; value: Send } | { type: 'toggle-key' } | { type: 'run' } | { type: 'reset' };
export function idempotentTransition(
  state: IdempotentState,
  action: IdempotentAction,
): IdempotentState {
  switch (action.type) {
    case 'reset':
      return initialIdempotent();
    case 'send':
      return { ...state, send: action.value, sent: false };
    case 'toggle-key':
      return { ...state, keyed: !state.keyed, sent: false };
    case 'run':
      return { ...state, sent: true };
  }
}
export const IDEMPOTENT_START_MESSAGE = 'Choose what happens, then press Send.';
export const IDEMPOTENT_ONCE_MESSAGE = 'One send makes one run and writes six rows.';
export const IDEMPOTENT_TWICE_MESSAGE =
  'The second press ran the turn again and wrote six more rows.';
export const IDEMPOTENT_TWICE_KEY_MESSAGE =
  'The second press matched the key and got the stored answer.';
export const IDEMPOTENT_RETRY_MESSAGE =
  'The retry started a second run while the first was still going.';
export const IDEMPOTENT_RETRY_KEY_MESSAGE =
  'The key matched a run still going, so no second run started.';
export const IDEMPOTENT_COUNTEREXAMPLE = 'A second run could propose the same write twice.';
export const IDEMPOTENT_REFINEMENT = 'Refinement: a key per turn, the stored answer on a repeat.';
export function idempotentSummary(state: IdempotentState): Summary {
  if (!state.sent) return { tone: 'neutral', message: IDEMPOTENT_START_MESSAGE };
  if (state.send === 'once') return { tone: 'success', message: IDEMPOTENT_ONCE_MESSAGE };
  if (state.send === 'twice')
    return state.keyed
      ? { tone: 'success', message: IDEMPOTENT_TWICE_KEY_MESSAGE }
      : { tone: 'warning', message: IDEMPOTENT_TWICE_MESSAGE };
  return state.keyed
    ? { tone: 'success', message: IDEMPOTENT_RETRY_KEY_MESSAGE }
    : { tone: 'warning', message: IDEMPOTENT_RETRY_MESSAGE };
}
